import { useRef } from 'react';
import { Camera, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EditableAvatarProps {
  // Core props
  image?: string;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  display?: 'circle' | 'square';
  hasUploadedImage?: boolean; // New prop to distinguish between real and default images

  // Callbacks
  onImageChange: (image: string) => void | Promise<void>;
  onImageRemove?: () => void | Promise<void>;

  // Customization
  className?: string;
  fallback?: React.ReactNode;

  // Validation/Processing
  maxSize?: number;
  acceptedTypes?: string[];
  compress?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

const sizeClasses = {
  sm: 'h-10 w-10',
  md: 'h-14 w-14',
  lg: 'h-24 w-24'
};

export function EditableAvatar({
  image,
  name,
  size = 'md',
  display = 'circle',
  hasUploadedImage = false,
  onImageChange,
  onImageRemove,
  className,
  fallback,
  maxSize = 2 * 1024 * 1024, // 2MB default
  acceptedTypes = ['image/jpeg', 'image/png', 'image/gif'],
  disabled = false,
  isLoading = false
}: EditableAvatarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize) {
      // TODO: Add proper error handling/messaging
      return;
    }

    // Validate file type
    if (!acceptedTypes.includes(file.type)) {
      // TODO: Add proper error handling/messaging
      return;
    }

    try {
      // For now, just use basic FileReader
      // TODO: Add image compression if compress=true
      const reader = new FileReader();
      reader.onloadend = () => {
        void onImageChange(reader.result as string);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      // TODO: Add proper error handling/messaging
      console.error('Failed to process image:', error);
    }
  };

  const displayClass = display === 'circle' ? 'rounded-full' : 'rounded-lg';

  const handleClick = () => {
    if (!disabled && !isLoading) {
      fileInputRef.current?.click();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={cn('relative group', className)}>
      {/* Avatar Container */}
      <div
        className={cn(
          'relative cursor-pointer',
          sizeClasses[size],
          'group-hover:ring-2 group-hover:ring-primary/50',
          'transition-all duration-200',
          displayClass
        )}
        onClick={handleClick}
      >
        {/* Base Layer: Always visible fallback */}
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-secondary text-secondary-foreground text-xl font-medium',
            displayClass
          )}
        >
          {fallback ?? getInitials(name)}
        </div>

        {/* Middle Layer: Image when available */}
        {image && (
          <img
            src={image}
            alt={name}
            className={cn('absolute inset-0 h-full w-full object-cover', displayClass)}
          />
        )}

        {/* Top Layer: Camera overlay or loading spinner */}
        {!disabled && (
          <>
            {isLoading ? (
              <div
                className={cn(
                  'absolute inset-0 flex items-center justify-center rounded-full bg-black/40',
                  displayClass
                )}
              >
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              </div>
            ) : (
              <div
                className={cn(
                  'absolute inset-0 z-20 flex items-center justify-center rounded-full',
                  'bg-black/0 group-hover:bg-black/40',
                  'transition-all duration-200'
                )}
              >
                <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-all duration-200" />
              </div>
            )}

            {/* Remove button */}
            {hasUploadedImage && !isLoading && onImageRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void onImageRemove();
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className={cn(
                  'absolute -top-1 -right-1 z-30 p-1.5 rounded-full',
                  'opacity-0 group-hover:opacity-100',
                  'bg-background border border-border',
                  'hover:bg-destructive hover:border-destructive hover:text-white',
                  'transition-all duration-200',
                  'shadow-sm'
                )}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
}
